/**
 * StarWebPRNT サービス
 *
 * ブラウザからLAN経由でStar mC-Print3に直接印刷する。
 * CloudPRNT（サーバーポーリング型）ではなく、StarWebPRNT（HTTP直結型）を使用。
 * - インターネット不要（LAN内で完結）
 * - 遅延なし（直接通信）
 * - オフラインでも印刷可能
 */

import printQueue from './printQueue';

const PRINTER_PORT = 80; // StarWebPRNTのHTTPポート（デフォルト）
const WEBPRNT_PATH = '/StarWebPRNT/SendMessage';
const TIMEOUT_MS = 5000;

// プリンタ設定のローカルストレージキー
const STORAGE_KEY = 'posplus_printer_settings';
const ROUTING_STORAGE_KEY = 'posplus_printer_routing';

/**
 * プリンタ設定を取得
 */
const getPrinterSettings = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.error('[StarWebPRNT] 設定の読み込みに失敗:', e);
  }
  return {
    receiptPrinter: { ip: '', port: PRINTER_PORT, enabled: false },
    kitchenPrinter: { ip: '', port: PRINTER_PORT, enabled: false },
    paperWidth: 80, // 80mm
    autoCut: true,
    openDrawer: true,
    autoKitchenPrint: true, // 注文時に自動でキッチン印刷
  };
};

/**
 * プリンタ設定を保存
 */
const savePrinterSettings = (settings) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    return true;
  } catch (e) {
    console.error('[StarWebPRNT] 設定の保存に失敗:', e);
    return false;
  }
};

/**
 * StarWebPRNT XMLリクエストを構築
 */
const buildStarXml = (commands) => {
  let xml = '<?xml version="1.0" encoding="UTF-8"?>';
  xml += '<StarWebPrint xmlns="http://www.star-m.jp" xmlns:i="http://www.star-m.jp/StarWebPRNT">';
  xml += commands;
  xml += '</StarWebPrint>';
  return xml;
};

/**
 * ESC/POSコマンドをStarWebPRNT XML形式に変換
 */
const buildReceiptXml = (receiptData, options = {}) => {
  const { autoCut = true, openDrawer = false } = options;
  let cmds = '';

  // 初期化
  cmds += '<i:initialization />';

  // 店名（センタリング・太字・大きい文字）
  if (receiptData.storeName) {
    cmds += '<i:alignment position="center" />';
    cmds += '<i:bold />';
    cmds += '<i:font-size width="2" height="2" />';
    cmds += `<i:text>${escapeXml(receiptData.storeName)}&#10;</i:text>`;
    cmds += '<i:font-size width="1" height="1" />';
    cmds += '<i:bold-off />';
  }

  // 住所・電話番号
  if (receiptData.storeAddress) {
    cmds += '<i:alignment position="center" />';
    cmds += `<i:text>${escapeXml(receiptData.storeAddress)}&#10;</i:text>`;
  }
  if (receiptData.storePhone) {
    cmds += `<i:text>TEL: ${escapeXml(receiptData.storePhone)}&#10;</i:text>`;
  }

  // インボイス登録番号
  if (receiptData.invoiceNumber) {
    cmds += `<i:text>登録番号: ${escapeXml(receiptData.invoiceNumber)}&#10;</i:text>`;
  }

  // 区切り線
  cmds += '<i:alignment position="left" />';
  cmds += '<i:text>--------------------------------&#10;</i:text>';

  // 日時
  const dateStr = receiptData.date || new Date().toLocaleString('ja-JP');
  cmds += `<i:text>${escapeXml(dateStr)}&#10;</i:text>`;

  // テーブル番号
  if (receiptData.tableNumber) {
    cmds += `<i:text>テーブル: ${escapeXml(String(receiptData.tableNumber))}&#10;</i:text>`;
  }

  cmds += '<i:text>--------------------------------&#10;</i:text>';

  // 注文明細
  if (receiptData.items && receiptData.items.length > 0) {
    for (const item of receiptData.items) {
      const name = typeof item.name === 'object' ? (item.name.ja || item.name.en || '') : (item.name || '');
      const qty = item.quantity || 1;
      const price = item.price || 0;
      const lineTotal = price * qty;

      cmds += `<i:text>${escapeXml(name)}&#10;</i:text>`;
      cmds += `<i:text>  ${qty}点 x ¥${price.toLocaleString()}`;
      cmds += `<i:alignment position="right" />`;
      cmds += ` ¥${lineTotal.toLocaleString()}&#10;</i:text>`;
      cmds += '<i:alignment position="left" />';
    }
  }

  cmds += '<i:text>--------------------------------&#10;</i:text>';

  // 小計・税・合計
  if (receiptData.subtotal !== undefined) {
    cmds += buildAmountLine('小計', receiptData.subtotal);
  }

  // 税率区分別
  if (receiptData.taxBreakdown) {
    for (const tb of receiptData.taxBreakdown) {
      cmds += buildAmountLine(`(${tb.rate}%対象)`, tb.taxableAmount);
      cmds += buildAmountLine(`  消費税`, tb.taxAmount);
    }
  } else if (receiptData.tax !== undefined) {
    cmds += buildAmountLine('消費税', receiptData.tax);
  }

  // 合計（太字・大文字）
  cmds += '<i:bold />';
  cmds += '<i:font-size width="2" height="2" />';
  cmds += buildAmountLine('合計', receiptData.total || 0);
  cmds += '<i:font-size width="1" height="1" />';
  cmds += '<i:bold-off />';

  // お預かり・お釣り
  if (receiptData.receivedAmount) {
    cmds += '<i:text>--------------------------------&#10;</i:text>';
    cmds += buildAmountLine('お預かり', receiptData.receivedAmount);
    cmds += buildAmountLine('お釣り', receiptData.change || 0);
  }

  // 決済方法
  if (receiptData.paymentMethod) {
    cmds += '<i:text>--------------------------------&#10;</i:text>';
    cmds += `<i:text>決済: ${escapeXml(receiptData.paymentMethod)}&#10;</i:text>`;
  }

  cmds += '<i:text>--------------------------------&#10;</i:text>';

  // フッター
  cmds += '<i:alignment position="center" />';
  cmds += '<i:text>ありがとうございました&#10;</i:text>';
  cmds += '<i:text>またのご来店をお待ちしております&#10;</i:text>';

  // QRコード（電子レシートURL）
  if (receiptData.receiptUrl) {
    cmds += '<i:feed line="1" />';
    cmds += `<i:qrcode model="2" level="M" cell="4">${escapeXml(receiptData.receiptUrl)}</i:qrcode>`;
    cmds += '<i:feed line="1" />';
  }

  cmds += '<i:feed line="3" />';

  // キャッシュドロア
  if (openDrawer) {
    cmds += '<i:cash-drawer channel="1" />';
  }

  // オートカット
  if (autoCut) {
    cmds += '<i:cut type="partial" />';
  }

  return buildStarXml(cmds);
};

/**
 * キッチンチケット用XML
 */
const buildKitchenTicketXml = (ticketData) => {
  let cmds = '';

  cmds += '<i:initialization />';

  // ヘッダー
  cmds += '<i:alignment position="center" />';
  cmds += '<i:bold />';
  cmds += '<i:font-size width="2" height="2" />';
  cmds += '<i:text>** 調理チケット **&#10;</i:text>';
  cmds += '<i:font-size width="1" height="1" />';
  cmds += '<i:bold-off />';

  // テーブル番号（大きく）
  if (ticketData.tableNumber) {
    cmds += '<i:font-size width="3" height="3" />';
    cmds += '<i:bold />';
    cmds += `<i:text>T-${escapeXml(String(ticketData.tableNumber))}&#10;</i:text>`;
    cmds += '<i:font-size width="1" height="1" />';
    cmds += '<i:bold-off />';
  }

  cmds += '<i:alignment position="left" />';
  cmds += '<i:text>--------------------------------&#10;</i:text>';

  // 時刻
  const timeStr = new Date().toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
  cmds += `<i:text>時刻: ${timeStr}&#10;</i:text>`;
  cmds += '<i:text>--------------------------------&#10;</i:text>';

  // 注文明細
  if (ticketData.items) {
    for (const item of ticketData.items) {
      const name = typeof item.name === 'object' ? (item.name.ja || '') : (item.name || '');
      const qty = item.quantity || 1;
      cmds += '<i:bold />';
      cmds += '<i:font-size width="2" height="1" />';
      cmds += `<i:text>${qty}x ${escapeXml(name)}&#10;</i:text>`;
      cmds += '<i:font-size width="1" height="1" />';
      cmds += '<i:bold-off />';

      if (item.note) {
        cmds += `<i:text>  ※${escapeXml(item.note)}&#10;</i:text>`;
      }
    }
  }

  cmds += '<i:text>--------------------------------&#10;</i:text>';
  cmds += '<i:feed line="3" />';
  cmds += '<i:cut type="partial" />';

  return buildStarXml(cmds);
};

/**
 * XMLエスケープ
 */
const escapeXml = (str) => {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
};

/**
 * 金額行を構築（名前 ... 金額 の形式）
 */
const buildAmountLine = (label, amount) => {
  return `<i:text>${escapeXml(label)}  ¥${Number(amount).toLocaleString()}&#10;</i:text>`;
};

/**
 * StarWebPRNTへHTTPリクエストを送信
 */
const sendToPrinter = async (printerConfig, xmlData) => {
  const { ip, port = PRINTER_PORT } = printerConfig;

  if (!ip) {
    throw new Error('プリンタIPアドレスが設定されていません');
  }

  const url = `http://${ip}:${port}${WEBPRNT_PATH}`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/xml; charset=UTF-8',
      },
      body: xmlData,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`プリンタ応答エラー: HTTP ${response.status}`);
    }

    const responseText = await response.text();

    // レスポンスからステータスを確認
    if (responseText.includes('ASB_PRINT_SUCCESS') || responseText.includes('SUCCESS')) {
      return { success: true };
    }

    // エラーの場合でもレスポンスが返ればある程度成功とみなす
    console.warn('[StarWebPRNT] 印刷レスポンス:', responseText);
    return { success: true, warning: responseText };

  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('プリンタ接続タイムアウト（5秒）');
    }
    throw error;
  }
};

/**
 * レシート印刷（フォールバック付き）
 * プリンタ障害時は電子レシートに自動切替
 */
const printReceipt = async (receiptData, options = {}) => {
  const settings = getPrinterSettings();
  const printerConfig = settings.receiptPrinter;

  if (!printerConfig.enabled || !printerConfig.ip) {
    console.log('[StarWebPRNT] レシートプリンタ未設定、スキップ');
    return { success: false, reason: 'printer_not_configured', fallback: true };
  }

  const xmlData = buildReceiptXml(receiptData, {
    autoCut: settings.autoCut,
    openDrawer: settings.openDrawer && receiptData.paymentMethod === '現金',
  });

  try {
    const result = await sendToPrinter(printerConfig, xmlData);
    console.log('[StarWebPRNT] レシート印刷成功');
    return { success: true, ...result };
  } catch (error) {
    console.error('[StarWebPRNT] レシート印刷失敗:', error.message);
    // 印刷キューに追加してリトライ対象にする
    printQueue.enqueue({
      type: 'receipt',
      printerConfig,
      xmlData,
      metadata: { storeName: receiptData.storeName, total: receiptData.total },
    }).catch(() => {});
    return {
      success: false,
      reason: 'printer_error',
      error: error.message,
      fallback: true,
      queued: true,
    };
  }
};

/**
 * キッチンチケット印刷
 */
const printKitchenTicket = async (ticketData) => {
  const settings = getPrinterSettings();
  const printerConfig = settings.kitchenPrinter;

  if (!printerConfig.enabled || !printerConfig.ip) {
    console.log('[StarWebPRNT] キッチンプリンタ未設定、スキップ');
    return { success: false, reason: 'printer_not_configured' };
  }

  const xmlData = buildKitchenTicketXml(ticketData);

  try {
    const result = await sendToPrinter(printerConfig, xmlData);
    console.log('[StarWebPRNT] キッチンチケット印刷成功');
    return { success: true, ...result };
  } catch (error) {
    console.error('[StarWebPRNT] キッチンチケット印刷失敗:', error.message);
    printQueue.enqueue({
      type: 'kitchen',
      printerConfig,
      xmlData,
      metadata: { tableNumber: ticketData.tableNumber },
    }).catch(() => {});
    return { success: false, reason: 'printer_error', error: error.message, queued: true };
  }
};

/**
 * キャッシュドロアを開く
 */
const openCashDrawer = async () => {
  const settings = getPrinterSettings();
  const printerConfig = settings.receiptPrinter;

  if (!printerConfig.enabled || !printerConfig.ip) {
    return { success: false, reason: 'printer_not_configured' };
  }

  const xml = buildStarXml('<i:cash-drawer channel="1" />');

  try {
    await sendToPrinter(printerConfig, xml);
    return { success: true };
  } catch (error) {
    console.error('[StarWebPRNT] ドロア開放失敗:', error.message);
    return { success: false, error: error.message };
  }
};

/**
 * プリンタの接続状態を確認
 */
const checkPrinterStatus = async (printerConfig) => {
  if (!printerConfig?.ip) {
    return { connected: false, reason: 'no_ip' };
  }

  const xml = buildStarXml('<i:initialization />');

  try {
    await sendToPrinter(printerConfig, xml);
    return { connected: true };
  } catch (error) {
    return { connected: false, reason: error.message };
  }
};

/**
 * 振り分けルールを取得
 * @returns {{ categoryId: string, printerId: string, printerIp: string, printerPort: number }[]}
 */
const getRoutingRules = () => {
  try {
    const saved = localStorage.getItem(ROUTING_STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.error('[StarWebPRNT] 振り分けルールの読み込みに失敗:', e);
  }
  return [];
};

/**
 * 振り分けルールを保存
 */
const saveRoutingRules = (rules) => {
  try {
    localStorage.setItem(ROUTING_STORAGE_KEY, JSON.stringify(rules));
    return true;
  } catch (e) {
    console.error('[StarWebPRNT] 振り分けルールの保存に失敗:', e);
    return false;
  }
};

/**
 * 自動キッチン印刷が有効か確認
 */
const isAutoKitchenPrintEnabled = () => {
  const settings = getPrinterSettings();
  return settings.autoKitchenPrint !== false && settings.kitchenPrinter?.enabled;
};

/**
 * 振り分けルール付きキッチン印刷
 * カテゴリに応じて異なるプリンタへ伝票を送る。
 * ルール未設定の商品はデフォルトのキッチンプリンタへ送る。
 */
const printKitchenTicketWithRouting = async (ticketData) => {
  if (!isAutoKitchenPrintEnabled()) {
    console.log('[StarWebPRNT] 自動キッチン印刷が無効です');
    return { success: false, reason: 'auto_print_disabled' };
  }

  const settings = getPrinterSettings();
  const rules = getRoutingRules();

  // ルールがない場合はデフォルトのキッチンプリンタに全品送る
  if (rules.length === 0) {
    return printKitchenTicket(ticketData);
  }

  // カテゴリ別にアイテムを振り分け
  const printerGroups = {};
  const defaultPrinterKey = `${settings.kitchenPrinter.ip}:${settings.kitchenPrinter.port}`;

  for (const item of (ticketData.items || [])) {
    const categoryId = item.categoryId || item.category_id;
    const rule = categoryId ? rules.find(r => String(r.categoryId) === String(categoryId)) : null;

    let printerKey;
    let printerConfig;

    if (rule && rule.printerIp) {
      printerKey = `${rule.printerIp}:${rule.printerPort || PRINTER_PORT}`;
      printerConfig = { ip: rule.printerIp, port: rule.printerPort || PRINTER_PORT };
    } else {
      printerKey = defaultPrinterKey;
      printerConfig = settings.kitchenPrinter;
    }

    if (!printerGroups[printerKey]) {
      printerGroups[printerKey] = { config: printerConfig, items: [] };
    }
    printerGroups[printerKey].items.push(item);
  }

  // 各プリンタへ並行して印刷
  const results = await Promise.allSettled(
    Object.entries(printerGroups).map(([, group]) => {
      const groupTicket = { ...ticketData, items: group.items };
      const xmlData = buildKitchenTicketXml(groupTicket);
      return sendToPrinter(group.config, xmlData);
    })
  );

  const failures = results.filter(r => r.status === 'rejected');
  if (failures.length > 0) {
    console.warn('[StarWebPRNT] 一部キッチンプリンタへの送信に失敗:', failures.length);
  }

  return {
    success: failures.length === 0,
    totalPrinters: results.length,
    failed: failures.length,
  };
};

/**
 * 印刷キューのリトライタイマーを開始
 */
const startPrintQueueRetry = () => {
  printQueue.startRetryTimer(sendToPrinter);
};

/**
 * 未処理の印刷キューを手動でリトライ
 */
const retryPrintQueue = () => {
  return printQueue.processQueue(sendToPrinter);
};

/**
 * 印刷キューの状態を取得
 */
const getPrintQueueStatus = async () => {
  const pending = await printQueue.getPendingJobs();
  const all = await printQueue.getAllJobs();
  return {
    pending: pending.length,
    total: all.length,
    jobs: all,
  };
};

const starWebPRNTService = {
  getPrinterSettings,
  savePrinterSettings,
  printReceipt,
  printKitchenTicket,
  printKitchenTicketWithRouting,
  openCashDrawer,
  checkPrinterStatus,
  buildReceiptXml,
  buildKitchenTicketXml,
  getRoutingRules,
  saveRoutingRules,
  isAutoKitchenPrintEnabled,
  startPrintQueueRetry,
  retryPrintQueue,
  getPrintQueueStatus,
};

export default starWebPRNTService;
